import React, { useState } from "react";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { FileDownloadOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import moment from "moment";

import { useLazyListShipmentStaleProvinceModeQuery } from "store/services/shipmentApi";
import { useAuth } from "hooks";
import { exportExcelFile } from "utils/exportExcelFile";

const getErrorMessage = (error) =>
  error?.data?.message || error?.message || "Unknown error";

const getSafeFileNamePart = (value = "") =>
  String(value || "All")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_") || "All";

const formatUnattendedDuration = (hoursSinceLastProcess) => {
  const hours = Math.floor(hoursSinceLastProcess || 0);
  const days = Math.floor(hours / 24);
  return days > 0 ? `${days}d ${hours % 24}h` : `${hours}h`;
};

const ExportStaleProvinceModeExcel = ({
  level,
  requestParams = {},
  search = "",
  scopeName = "",
  provinceName = "",
  branchName = "",
  isBusy = false,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { isVendor, isCustomer } = useAuth();
  const [fetchProvinceModeData] = useLazyListShipmentStaleProvinceModeQuery();

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const response = await fetchProvinceModeData({
        ...requestParams,
        page: 1,
        limit: 10000,
        exportAll: true,
      }).unwrap();

      let data = response?.data ?? [];

      if (level === "provinces" && search.trim()) {
        const keyword = search.trim().toLowerCase();
        data = data.filter(
          (row) =>
            row?.name?.toLowerCase().includes(keyword) ||
            row?.code?.toLowerCase().includes(keyword)
        );
      }

      if (!data.length) {
        toast.info(
          "No unattended shipment records found for the selected filters."
        );
        return;
      }

      if (level === "shipments" && Number(response?.totalItems) > data.length) {
        toast.warning(
          `Excel export was limited to the first ${data.length} of ${response.totalItems} matching shipments.`
        );
      }

      const statusPart = getSafeFileNamePart(requestParams.status);
      const scopePart = getSafeFileNamePart(
        branchName || provinceName || scopeName
      );
      let formattedData = [];

      if (level === "provinces") {
        formattedData = data.map((row, index) => ({
          SN: index + 1,
          Province: row.name || "N/A",
          "Province Code": row.code || "N/A",
          "Unattended Shipments": row.count ?? 0,
        }));
      } else if (level === "branches") {
        formattedData = data.map((row, index) => ({
          SN: index + 1,
          Province: provinceName || scopeName || "N/A",
          Branch: row.name || "N/A",
          "Branch Code": row.code || "N/A",
          "Unattended Shipments": row.count ?? 0,
        }));
      } else {
        formattedData = data.map((row, index) => ({
          SN: index + 1,
          CreatedAt: row.createdAt
            ? moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss")
            : "N/A",
          "Shipment ID": row.shipmentId || "N/A",
          Province: provinceName || "N/A",
          Branch: branchName || "N/A",
          "Source Branch": row.sourceBranch?.name || "N/A",
          "Destination Branch": row.destinationBranch?.name || "N/A",
          Receiver:
            row.receiver?.customerUser?.name || row.receiver?.name || "N/A",
          Sender:
            !isVendor && !isCustomer
              ? row.customerId?.customerUser?.name
                ? `CUS: ${row.customerId.customerUser.name}`
                : row.vendor?.shopName
                ? `VEN: ${row.vendor.shopName}`
                : "N/A"
              : "N/A",
          "Shipment Status": row.status || "N/A",
          "Unattended Duration": formatUnattendedDuration(
            row.hoursSinceLastProcess
          ),
          "Last Process Date": row.lastProcessDate
            ? moment(row.lastProcessDate).format("YYYY-MM-DD HH:mm:ss")
            : "N/A",
          "Created By": row.createdBy?.name || "N/A",
        }));
      }

      const exportType =
        level === "provinces"
          ? "Provinces"
          : level === "branches"
          ? "Branches"
          : "Shipments";

      exportExcelFile({
        sheetData: formattedData,
        fileName: `Unattended_${exportType}_${scopePart}_${statusPart}_${moment().format(
          "YYYYMMDD"
        )}`,
      });

      toast.success(
        `Successfully downloaded ${
          formattedData.length
        } unattended shipment record${formattedData.length === 1 ? "" : "s"}.`
      );
    } catch (error) {
      console.error("Error exporting unattended shipment data:", error);
      toast.error(
        "Error exporting unattended shipment data: " + getErrorMessage(error)
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Tooltip title="Export current view to Excel" arrow>
      <span>
        <IconButton
          color="success"
          onClick={handleDownload}
          disabled={isBusy || isDownloading}
        >
          {isDownloading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <FileDownloadOutlined />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default ExportStaleProvinceModeExcel;
